import type { PostHog as ClientPostHog } from 'posthog-js';

import {
  SOURCE_APP_PROPERTY,
  type AnalyticsEventOptions,
  type PostHogImplementation,
} from '../posthog-analytics-service';
import { PostHogClientSingleton } from './posthog-client-singleton';

/**
 * PostHog analytics service that sends events to PostHog in the browser.
 */
export class ClientPostHogImpl implements PostHogImplementation {
  private ph: ClientPostHog | undefined;
  private initFailed = false;

  constructor(
    private key: string,
    private host: string,
    private ingestUrl?: string,
  ) {}

  async initialize() {
    this.ph = await PostHogClientSingleton.getInstance({
      key: this.key,
      host: this.host,
      ingestUrl: this.ingestUrl,
    });
  }

  async identify(userId: string, traits?: Record<string, string>) {
    const client = await this.getClient();

    if (!client) {
      this.log('PostHog not ready — skipping identify');
      return;
    }

    client.identify(userId, traits);
  }

  async group(
    groupType: string,
    groupKey: string,
    groupProperties?: Record<string, string>,
  ) {
    const client = await this.getClient();

    if (!client) {
      this.log('PostHog not ready — skipping group');
      return;
    }

    client.group(groupType, groupKey, groupProperties);
  }

  async trackPageView(url: string) {
    const client = await this.getClient();

    if (!client) {
      this.log('PostHog not ready — skipping trackPageView');
      return;
    }

    client.capture('$pageview', { $current_url: url });
  }

  async trackEvent(
    eventName: string,
    eventProperties?: Record<string, string | string[]>,
    options?: AnalyticsEventOptions,
  ) {
    const client = await this.getClient();

    if (!client) {
      this.log('PostHog not ready — skipping trackEvent');
      return;
    }

    // Associating `$groups` on the event itself attributes it to the group
    // even if `group()` has not been called for this session yet.
    const properties = {
      ...eventProperties,
      ...(options?.groups ? { $groups: options.groups } : {}),
      // Normally redundant on the client — `source_app` is registered as a super
      // property at init, so every event already carries it. Honoured anyway so
      // an explicit per-call value isn't silently dropped.
      ...(options?.sourceApp
        ? { [SOURCE_APP_PROPERTY]: options.sourceApp }
        : {}),
    };

    client.capture(eventName, properties);
  }

  async registerProperties(properties: Record<string, string>) {
    const client = await this.getClient();

    if (!client) {
      this.log('PostHog not ready — skipping registerProperties');
      return;
    }

    // Super properties are attached to every subsequent event and persisted in
    // localStorage, so this closes the race where events fire before a tenant
    // identifier is attached per-call.
    client.register(properties);
  }

  async unregisterProperty(key: string) {
    const client = await this.getClient();

    if (!client) {
      this.log('PostHog not ready — skipping unregisterProperty');
      return;
    }

    client.unregister(key);
  }

  log(...args: unknown[]) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[ClientPostHog]', ...args);
    }
  }

  private async getClient() {
    // The analytics manager fires `initialize()` without awaiting it, so the
    // first page view / identify can land before the async `posthog-js` import
    // resolves. Await the (idempotent) initializer instead of throwing, so the
    // event is captured once the client is ready — concurrent early calls all
    // await the same in-flight init and then fire. If init genuinely fails we
    // record it, return undefined, and callers no-op: analytics is
    // fire-and-forget and must never crash the UI. `initFailed` is only set
    // after a real failure (never before the await), so it does not short-
    // circuit the concurrent-race path, and it stops us re-attempting a
    // known-failed init (and re-logging) on every subsequent event.
    if (!this.ph && !this.initFailed) {
      try {
        await this.initialize();
      } catch (error) {
        this.initFailed = true;
        this.log('PostHog initialization failed', error);
      }
    }

    return this.ph;
  }
}
